import React from "react";
import { FormGroup, Label, Col, Input } from "reactstrap";

type SortDirection = "asc" | "desc";

/** The stored sort: `null` means Popularity, descending. */
export type ProductSort = { property_id: string | null; direction: SortDirection } | null;

export type ProductSortControlProps = {
  // The stored value as it comes back from the page document; an ObjectId may
  // still be an object here, hence the String() below.
  productSort?: { property_id?: unknown; direction?: string } | null;
  properties?: { _id: unknown; name?: string }[];
  onChange: (next: ProductSort) => void;
};

/**
 * Editor control for the listing-page default product sort.
 *
 * Shown on category/listing pages (sub_category, not a generated tag page).
 * Stores `{ property_id, direction }` or null (Popularity DESC). The option
 * values are property ObjectIds, never display names.
 */
const ProductSortControl = ({ productSort, properties = [], onChange }: ProductSortControlProps) => {
  const propertyId = productSort?.property_id
    ? String(productSort.property_id)
    : "";
  const direction: SortDirection = productSort?.direction === "asc" ? "asc" : "desc";
  const knownIds = new Set(properties.map((p) => String(p._id)));
  const staleId = propertyId && !knownIds.has(propertyId) ? propertyId : null;

  const emit = (nextId: string, nextDirection: SortDirection) => {
    if (!nextId && nextDirection === "desc") {
      onChange(null);
      return;
    }
    onChange({
      property_id: nextId || null,
      direction: nextDirection,
    });
  };

  return (
    <FormGroup className="row" data-testid="product-sort-control">
      <Label className="form-control-label" md="2" htmlFor="product-sort-property">
        Default product sort
      </Label>
      <Col md="6">
        <Input
          id="product-sort-property"
          type="select"
          name="product_sort_property"
          value={propertyId}
          onChange={(e) => emit(e.target.value, direction)}
        >
          <option value="">Popularity (default)</option>
          {staleId ? (
            <option value={staleId}>
              Unavailable property (will use Popularity)
            </option>
          ) : null}
          {properties.map((prop) => (
            <option key={String(prop._id)} value={String(prop._id)}>
              {prop.name}
            </option>
          ))}
        </Input>
        {staleId ? (
          <div className="text-warning mt-1" data-testid="product-sort-stale">
            The selected property is no longer available. Save to restore the
            Popularity default, or pick another property.
          </div>
        ) : null}
      </Col>
      <Col md="4">
        <Input
          id="product-sort-direction"
          type="select"
          name="product_sort_direction"
          value={direction}
          // The select offers only the two options, so the value is one of them.
          onChange={(e) => emit(propertyId, e.target.value as SortDirection)}
          aria-label="Sort direction"
        >
          <option value="desc">Descending</option>
          <option value="asc">Ascending</option>
        </Input>
      </Col>
    </FormGroup>
  );
};

export default ProductSortControl;
